import React, { createContext, useContext, useState, useEffect } from 'react';
import { companiesApi, Company } from '../services/companies';
import { useAuth } from './AuthContext';

interface CompanyContextType {
  company: Company | null;
  loading: boolean;
  refreshCompany: () => Promise<void>;
  baseCurrency: string;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);

  const loadCompany = async () => {
    if (user?.companyId) {
      try {
        setLoading(true);
        const companyData = await companiesApi.getById(user.companyId);
        setCompany(companyData);
      } catch (error) {
        console.error('Failed to load company:', error);
        setCompany(null);
      } finally {
        setLoading(false);
      }
    } else {
      setCompany(null);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompany();
    
    // Listen for company update events
    const handleCompanyUpdate = () => {
      loadCompany();
    };
    window.addEventListener('companyUpdated', handleCompanyUpdate);
    
    return () => {
      window.removeEventListener('companyUpdated', handleCompanyUpdate);
    };
  }, [user?.companyId]);

  return (
    <CompanyContext.Provider
      value={{
        company,
        loading,
        refreshCompany: loadCompany,
        baseCurrency: company?.baseCurrency || 'USD',
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};







